import { Injectable } from '@angular/core';
import { SPECIALITIES, MedpraxTariffType, medpraxTariffType } from '../data/app.data';

export interface DisciplineConfig {
    disciplineCode: string;
    disciplineName: string;
    rateType: 'medical' | 'alternative' | 'physio';
    defaultModifiers: string[];
    supportedModifiers: string[];
    priceCalculationMethod: 'standard' | 'physio' | 'alternative';
    hasAnaestheticRules: boolean;
    hasEmergencyRules: boolean;
    hasAssistantRules: boolean;
}

@Injectable({ providedIn: 'root' })
export class DisciplineConfigService {
    private readonly disciplineConfigs: DisciplineConfig[] = [
        // Medical disciplines (default)
        {
            disciplineCode: 'medical',
            disciplineName: 'Medical Professionals',
            rateType: 'medical',
            defaultModifiers: ['0001', '0002', '0004', '0005', '0006', '0007', '0008', '0009', '0010', '0011', '0013', '0014', '0015'],
            supportedModifiers: [
                '0001', '0002', '0004', '0005', '0006', '0007', '0008', '0009', '0010', '0011', '0013', '0014', '0015', '0017', '0018', '0019',
                '0020', '0021', '0024', '0025', '0026', '0027', '0028', '0029', '0030', '0031', '0032', '0033', '0034', '0035', '0036', '0037', '0038', '0039',
                '0040', '0041', '0042', '0043', '0044', '0045', '0046', '0047', '0053', '0055', '0057', '0058', '0060', '0061', '0063', '0064', '0065', '0066', '0067', '0069',
                '0070', '0072', '0073', '0074', '0075', '0077', '0078', '0079', '0080', '0081', '0082', '0083', '0084', '0085', '0086', '0090', '0091', '0092', '0093', '0095', '0096', '0097', '0099',
                '0100', '0160', '0165', '01030', '5104', '5141', '5441', '5442', '5443', '5444', '5445', '5448', '6100', '6101', '6102', '6103', '6104', '6105', '6106', '6107', '6108', '6109', '6110',
                '6300', '6301', '6302', '6303', '6305'
            ],
            priceCalculationMethod: 'standard',
            hasAnaestheticRules: true,
            hasEmergencyRules: true,
            hasAssistantRules: true
        },
        // Physiotherapy discipline
        {
            disciplineCode: '072',
            disciplineName: 'Physiotherapy',
            rateType: 'alternative',
            defaultModifiers: ['0001', '0003', '0008', '0009', '0010', '0013', '0014'],
            supportedModifiers: [
                '0001', // Appointment not kept
                '0003', // 15% reduction for non-owned equipment
                '0008', // 50% reduction for additional procedures
                '0009', // Full fee for additional condition
                '0010', // 50% reduction for second condition
                '0013', // Travelling costs
                '0014'  // In-patient services
            ],
            priceCalculationMethod: 'physio',
            hasAnaestheticRules: false,
            hasEmergencyRules: false,
            hasAssistantRules: false
        }
    ];

    /**
     * Get configuration for a specific discipline code
     */
    getConfig(disciplineCode: string): DisciplineConfig {
        const config = this.disciplineConfigs.find(c => c.disciplineCode === disciplineCode);
        if (!config) {
            // Fallback to medical config
            return this.disciplineConfigs.find(c => c.disciplineCode === 'medical')!;
        }
        return config;
    }

    /**
     * Resolve the discipline code for a group speciality, falling back to medical
     */
    getDisciplineCodeForSpeciality(speciality?: string): string {
        const matchedSpeciality = SPECIALITIES.find(s => s.value === speciality);
        return matchedSpeciality?.discipline || 'medical';
    }

    /**
     * Get all supported discipline codes
     */
    getSupportedDisciplines(): string[] {
        return this.disciplineConfigs.map(c => c.disciplineCode);
    }

    /**
     * Check if a discipline supports a specific modifier
     */
    supportsModifier(disciplineCode: string, modifierCode: string): boolean {
        const config = this.getConfig(disciplineCode);
        return config.supportedModifiers.includes(modifierCode);
    }

    /**
     * Get the MedPrax tariff (medType) for a discipline. Derived from the discipline code rather
     * than getConfig(), which falls back to the medical config for every discipline except 072
     * and so sent other allied and dental disciplines to the medical tariff.
     */
    getRateType(disciplineCode: string): MedpraxTariffType {
        return medpraxTariffType(disciplineCode);
    }

    /**
     * Get default modifiers for a discipline
     */
    getDefaultModifiers(disciplineCode: string): string[] {
        const config = this.getConfig(disciplineCode);
        return config.defaultModifiers;
    }

    /**
     * Check if discipline has anaesthetic rules
     */
    hasAnaestheticRules(disciplineCode: string): boolean {
        const config = this.getConfig(disciplineCode);
        return config.hasAnaestheticRules;
    }

    /**
     * Check if discipline has emergency rules
     */
    hasEmergencyRules(disciplineCode: string): boolean {
        const config = this.getConfig(disciplineCode);
        return config.hasEmergencyRules;
    }

    /**
     * Check if discipline has assistant rules
     */
    hasAssistantRules(disciplineCode: string): boolean {
        const config = this.getConfig(disciplineCode);
        return config.hasAssistantRules;
    }
} 